import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { listMyOrganizations, listStoresForOrg } from '@pc/db';
import { useAuth } from './AuthProvider';

const LS_ORG = 'pc.activeOrgId';
const LS_STORE = 'pc.activeStoreId';

interface ActiveOrgValue {
  activeOrgId: string | null;
  activeStoreId: string | null;
  availableOrgIds: string[];
  ready: boolean;
  setActiveOrg(orgId: string): Promise<void>;
}

const Ctx = createContext<ActiveOrgValue | null>(null);

/**
 * Shop-side active-org tracker. Reuses the same localStorage keys as
 * apps/web (pc.activeOrgId / pc.activeStoreId) so a buyer landing on
 * shop from the operator app inherits their last selection.
 */
export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(null);
  const [availableOrgIds, setAvailableOrgIds] = useState<string[]>([]);
  const [ready, setReady] = useState(false);

  // Once-per-user hydrate guard. Without this, the [loading, session]
  // deps below would re-run the full org/store fetch on every supabase
  // auth event that ships a new Session object reference -- including
  // the no-op `SIGNED_IN` that `@supabase/auth-js` re-emits on every
  // tab visibility change. The AuthProvider's sessionsEqual guard
  // makes this redundant in the common path, but pinning to a userId
  // string is the bulletproof second line of defense (mirrors the
  // pattern in apps/web's ActiveContextProvider + apps/sell's
  // VendorContext).
  const hydratedForUserId = useRef<string | null>(null);

  const userId = session?.user?.id ?? null;

  useEffect(() => {
    if (loading) return;
    if (!userId) {
      hydratedForUserId.current = null;
      setReady(true);
      return;
    }
    if (hydratedForUserId.current === userId) return;
    hydratedForUserId.current = userId;
    let alive = true;
    (async () => {
      try {
        const orgs = await listMyOrganizations();
        if (!alive) return;
        const ids = orgs.map((o) => o.id);
        setAvailableOrgIds(ids);
        let saved = localStorage.getItem(LS_ORG);
        if (saved && !ids.includes(saved)) saved = null;
        const initial = saved ?? (ids.length > 0 ? ids[0]! : null);
        if (initial) {
          setActiveOrgId(initial);
          localStorage.setItem(LS_ORG, initial);
          const stores = await listStoresForOrg(initial);
          if (!alive) return;
          const storeIds = stores.map((s) => s.id);
          let savedStore = localStorage.getItem(LS_STORE);
          if (savedStore && !storeIds.includes(savedStore)) savedStore = null;
          const nextStore = savedStore ?? (storeIds[0] ?? null);
          setActiveStoreId(nextStore);
          if (nextStore) localStorage.setItem(LS_STORE, nextStore);
        }
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loading, userId]);

  const setActiveOrg = useCallback(async (orgId: string) => {
    setActiveOrgId(orgId);
    localStorage.setItem(LS_ORG, orgId);
    const stores = await listStoresForOrg(orgId);
    const next = stores[0]?.id ?? null;
    setActiveStoreId(next);
    if (next) localStorage.setItem(LS_STORE, next);
  }, []);

  const value = useMemo<ActiveOrgValue>(
    () => ({ activeOrgId, activeStoreId, availableOrgIds, ready, setActiveOrg }),
    [activeOrgId, activeStoreId, availableOrgIds, ready, setActiveOrg],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useActiveOrg(): ActiveOrgValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useActiveOrg must be used inside <ActiveOrgProvider>.');
  return v;
}
